import React, { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowBack, Cancel, CancelOutlined } from '@mui/icons-material';
import ProductItem from './components/Services/ProductItem';
import BasicModal from './components/Modal';
import { deleteProduct, getAppSetting, getProcessors, updateServices } from '../api/services';
import { getServices } from '../store/actions/services';
import AlertDialogSlide from './components/CustomAlert';
import { BRAND_IMAGES } from '../utils/assets';

export default function UpdateService() {
  const dispatch = useDispatch();
  const { category } = useParams();

  const navigate = useNavigate();

  const location = useLocation();

  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const currentService = queryParams.get('currentService');

  const [editedItem, setEditedItem] = React.useState(null);
  const [showEdit, setShowEdit] = React.useState(false);
  const [amount, setAmount] = React.useState('');
  const [vipAmount, setVipAmount] = React.useState('');
  const [isAirtimeSubService, setIsAirtimeSubService] = useState(false);
  const [cashback, setCashback] = React.useState('');
  const [referralBonus, setReferralBonus] = React.useState('');
  const [buyingAmount, setBuyingAmount] = React.useState('');
  const [validity, setValidity] = useState('');
  const [descriptions, setDescriptions] = useState('');

  const { services } = useSelector((state) => state.services);
  const [loading, setLoading] = React.useState(false);
  const [planId, setPlanId] = React.useState('');
  const [networkId, setNetworkId] = React.useState('');
  const [toggleSetPlanId, setToggleSetPlanId] = React.useState(false);

  const [identifier, setIdentifier] = useState('');
  const [currentProcessors, setCurrentProcessors] = useState([]);

  const selectedCategory = services.filter(
    (service) => service.category?.trim() === category.trim() && service.service === currentService
  );
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [discount, setDiscount] = useState(0.0);

  const [currentAvailability, setCurrentAvailability] = useState(null);
  const [currentVisibility, setCurrentVisibility] = useState(null);
  const [name, setName] = useState('');
  const [feeAmount, setFeeAmount] = useState(0);

  const goBack = () => navigate(-1);

  const showDiscount = selectedCategory[0]?.service === 'Airtime' || selectedCategory[0]?.service === 'Electricity';
  const canRemove = selectedCategory[0]?.service === 'Data';

  const handleEditServiceAmount = (service) => {
    setEditedItem(service);
    if (service.service === 'Data') {
      const selectedProcessor = service.processor.authorization.split('_TOKEN')[0].toLowerCase();
      setIdentifier(selectedProcessor);
    }
    setAmount(service.details?.amount || '');
    setVipAmount(service.vipAmount || '');
    setIsAirtimeSubService(service.isAirtimeSubService || false);
    setCashback(service.cashback || 0);
    setReferralBonus(service.referralBonus || 0);
    setName(service.details?.name || '');
    setDiscount(service.discount);
    setDescriptions(service.descriptions);
    setValidity(service.validity);
    setCurrentAvailability(service.available ? 'available' : 'unavailable');
    setCurrentVisibility(service.hidden ? 'hidden' : 'show');
    setBuyingAmount(service.buyingAmount || '');
    setShowEdit(true);
    setFeeAmount(service.fee || 0);
  };

  const getImageUrl = (item) => {
    if (item.service?.toLowerCase() !== 'data' && item.service?.toLowerCase() !== 'airtime') {
      return null;
    }

    return BRAND_IMAGES[item?.category?.toLowerCase()?.split(' ')[0]] || null;
  };

  const handleUpdateAmount = async () => {
    // Validate buying amount has to be number and not empty
    if (!buyingAmount || Number.isNaN(buyingAmount)) {
      toast.error('Please enter a valid buying amount');
      return;
    }

    const imageUrl = getImageUrl(editedItem);

    const payload = {
      serviceId: editedItem.id,
      amount: amount || 0,
      vipAmount,
      isAirtimeSubService,
      cashback,
      referralBonus,
      discount,
      buyingAmount,
      validity,
      fee: feeAmount,
      descriptions,
      available: currentAvailability === 'available',
      hidden: currentAvailability === 'hidden',
      changePlanId: false,
      name: name?.trim(),
    };

    if (imageUrl) {
      payload.imageUrl = imageUrl;
    }

    if (identifier && currentService === 'Data') {
      payload.serviceProcessor = identifier;
    }

    if (toggleSetPlanId && currentService === 'Data') {
      payload.planId = planId;
      payload.networkId = networkId;
      payload.changePlanId = true;
    }

    setLoading(true);

    const response = await updateServices(payload);
    if (response.ok) {
      setLoading(false);
      setShowEdit(false);
      toast.success(response.data.message);
      dispatch(getServices());
      return;
    }

    setLoading(false);
    toast.error(response.data.message);
  };

  const continueRemovingProduct = async (option) => {
    if (!option) {
      cancelRemoveProduct();
      return;
    }
    setDeleteLoading(true);

    const request = await deleteProduct(selectedProduct.id);
    if (request.ok) {
      toast.success('delete success');
      dispatch(getServices());
      setSelectedProduct(null);
      setDeleteLoading(false);
      return;
    }

    setDeleteLoading(false);
    setSelectedProduct(null);
    toast.error('Error while deleting');
  };

  const cancelRemoveProduct = () => {
    setSelectedProduct(null);
  };

  const handleRemoveProduct = (product) => {
    setSelectedProduct(product);
  };

  const handleGetAppSetting = useCallback(async () => {
    const request = await getAppSetting();
    if (request.ok) {
      const { data } = request.data;
      setCurrentProcessors(data?.service_processor?.current_processors || []);
    }
  }, []);

  React.useEffect(() => {
    handleGetAppSetting();
  }, []);

  return (
    <>
      <BasicModal isOpen={showEdit} toggleOpen={() => setShowEdit(!showEdit)}>
        <Stack spacing={2} style={{ height: '70vh', overflowY: 'scroll' }}>
          <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
            <Typography variant="h6">Update Product</Typography>
            <IconButton onClick={() => setShowEdit(!showEdit)}>
              <Cancel color="error" />
            </IconButton>
          </Stack>
          {editedItem && editedItem.service === 'Data' && (
            <>
              <FormControl>
                <InputLabel>Processor</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={identifier}
                  label="Processor"
                  onChange={(e) => {
                    setIdentifier(e.target.value);
                  }}
                >
                  {currentProcessors?.map((pr) => (
                    <MenuItem key={pr} value={pr.identifier}>
                      {pr.name} - {pr.identifier}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* If it is Data category only show this switch for airtime sub-service */}
              {editedItem?.service?.toLowerCase() === 'data' && (
                <FormControlLabel
                  control={
                    <Switch checked={isAirtimeSubService} onChange={(e) => setIsAirtimeSubService(e.target.checked)} />
                  }
                  label="Airtime Sub Service"
                />
              )}

              {/* Setup user to be able to change plan id after toggling that  */}
              <Button
                variant="outlined"
                startIcon={<CancelOutlined />}
                onClick={() => {
                  setToggleSetPlanId(!toggleSetPlanId);
                  if (toggleSetPlanId) {
                    setPlanId('');
                    setNetworkId('');
                  }
                }}
              >
                {toggleSetPlanId ? `Toggle use new Plan ID: ON` : 'Toggle use new Plan ID: OFF'}
              </Button>

              {toggleSetPlanId && (
                <>
                  <TextField
                    fullWidth
                    sx={{ my: '20px' }}
                    id="outlined-required"
                    label="Network ID"
                    type="text"
                    value={networkId}
                    onChange={(event) => {
                      setNetworkId(event.target.value);
                    }}
                  />
                  <TextField
                    fullWidth
                    sx={{ my: '20px' }}
                    id="outlined-required"
                    label="Plan ID"
                    type="text"
                    value={planId}
                    onChange={(event) => {
                      setPlanId(event.target.value);
                    }}
                  />
                </>
              )}
            </>
          )}
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel id="demo-simple-select-label">Product Availability</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={currentAvailability}
              label="Product Availability"
              onChange={(event) => setCurrentAvailability(event.target.value)}
            >
              <MenuItem value={'available'}>Available</MenuItem>
              <MenuItem value={'unavailable'}>Unavailable</MenuItem>
            </Select>
          </FormControl>
          <TextField
            name="planName"
            value={name}
            label="Name"
            onChange={(e) => {
              const { value } = e.target;
              // Only allow alphanumeric characters (letters and numbers)
              if (/^[a-zA-Z0-9\s.]*$/.test(value)) {
                setName(value);
              }
            }}
          />
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel id="demo-simple-select-label">Product Visibility</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={currentVisibility}
              label="Product Visibility"
              onChange={(event) => setCurrentVisibility(event.target.value)}
            >
              <MenuItem value={'show'}>Show Product</MenuItem>
              <MenuItem value={'hidden'}>Hide Product</MenuItem>
            </Select>
          </FormControl>
          {showDiscount ? (
            <>
              <TextField
                fullWidth
                sx={{ my: '20px' }}
                id="outlined-required"
                label="Discount"
                type="number"
                onPaste={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onCopy={(e) => {
                  e.preventDefault();
                  return false;
                }}
                value={discount}
                onChange={(event) => {
                  if (Number(event.target.value) || event.target.value === '' || event.target.value === '0') {
                    if (Number(event.target.value) >= 100) {
                      setDiscount(100);
                      return;
                    }
                    setDiscount(event.target.value);
                  }
                }}
              />
              <TextField
                fullWidth
                sx={{ my: '20px' }}
                id="outlined-required"
                label="Fee (Percentage)"
                type="number"
                onPaste={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onCopy={(e) => {
                  e.preventDefault();
                  return false;
                }}
                value={feeAmount}
                onChange={(event) => {
                  if (Number(event.target.value) || event.target.value === '' || event.target.value === '0') {
                    if (Number(event.target.value) >= 100) {
                      setFeeAmount(100);
                      return;
                    }
                    setFeeAmount(event.target.value);
                  }
                }}
              />
            </>
          ) : (
            <>
              <TextField
                fullWidth
                sx={{ my: '20px' }}
                id="outlined-required"
                label="Amount"
                type="number"
                onPaste={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onCopy={(e) => {
                  e.preventDefault();
                  return false;
                }}
                value={amount}
                onChange={(event) => {
                  if (Number(event.target.value) || event.target.value === '') {
                    setAmount(event.target.value);
                  }
                }}
              />
              <TextField
                fullWidth
                sx={{ my: '20px' }}
                id="outlined-required"
                label="Vip Amount"
                type="number"
                onPaste={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onCopy={(e) => {
                  e.preventDefault();
                  return false;
                }}
                value={vipAmount}
                onChange={(event) => {
                  if (Number(event.target.value) || event.target.value === '') {
                    setVipAmount(event.target.value);
                  }
                }}
              />
              <TextField
                fullWidth
                sx={{ my: '20px' }}
                id="outlined-required"
                label="Cashback"
                type="number"
                onPaste={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onCopy={(e) => {
                  e.preventDefault();
                  return false;
                }}
                value={cashback}
                onChange={(event) => {
                  if (Number(event.target.value) || event.target.value === '' || event.target.value === '0') {
                    setCashback(event.target.value);
                  }
                }}
              />
              <TextField
                fullWidth
                sx={{ my: '20px' }}
                id="outlined-required"
                label="Referral Bonus"
                type="number"
                onPaste={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onCopy={(e) => {
                  e.preventDefault();
                  return false;
                }}
                value={referralBonus}
                onChange={(event) => {
                  if (Number(event.target.value) || event.target.value === '' || event.target.value === '0') {
                    setReferralBonus(event.target.value);
                  }
                }}
              />
            </>
          )}

          <TextField
            fullWidth
            sx={{ my: '20px' }}
            id="outlined-required"
            label="Buying Price"
            type="number"
            onPaste={(e) => {
              e.preventDefault();
              return false;
            }}
            onCopy={(e) => {
              e.preventDefault();
              return false;
            }}
            value={buyingAmount}
            onChange={(event) => {
              setBuyingAmount(event.target.value);
            }}
          />

          {showDiscount && (
            <Typography variant="subtitle2" color="coral">
              For Airtime and Electricity orders, the <b>Buying Price</b> represents the
              <b> profit percentage</b> you earn on each sale (e.g. 1% of ₦100 = ₦1 profit).
            </Typography>
          )}

          <TextField
            name="validity"
            label="Validity"
            value={validity}
            onChange={(e) => {
              setValidity(e.target.value);
            }}
          />

          <TextField
            name="description"
            label="Description"
            multiline
            minRows={3}
            value={descriptions}
            onChange={(e) => {
              setDescriptions(e.target.value);
            }}
          />

          <Stack spacing={'10px'}>
            <Button variant={'contained'} marginLeft="10px" onClick={handleUpdateAmount} disabled={loading}>
              {loading ? <CircularProgress size="20px" /> : 'Update Service'}
            </Button>
            <Button variant={'outlined'} onClick={() => setShowEdit(false)}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      </BasicModal>
      <AlertDialogSlide
        dialogTitle={selectedProduct && `Delete ${selectedProduct.service} Item`}
        dialog={
          selectedProduct &&
          `Are you sure you want to remove ${selectedProduct.category} ${selectedProduct.details?.name || ''}`
        }
        loading={deleteLoading}
        onClose={cancelRemoveProduct}
        onOption={continueRemovingProduct}
        isOpen={selectedProduct !== null}
      />
      <Box padding={'20px'}>
        <Stack direction={'row'} alignItems={'center'}>
          <IconButton onClick={goBack}>
            <ArrowBack />
          </IconButton>
          <Typography>Go back</Typography>
        </Stack>
        <Typography variant="h4">Update {category}</Typography>
        <Grid container spacing={'10px'} mt={2}>
          {selectedCategory.map((item) => (
            <Grid key={item.id} item xs={6} sm={2}>
              <ProductItem
                canRemove={canRemove}
                onRemoveProduct={() => handleRemoveProduct(item)}
                onItemPress={() => handleEditServiceAmount(item)}
                title={`${category} ${showDiscount ? '' : item.details.name}`}
                amount={item.details.amount}
                discount={item?.discount}
                imageUrl={item.imageUrl}
                showDiscount={showDiscount}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </>
  );
}
